import { useState } from "react";
import { Copy, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { copyText } from "@/components/PdPill";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DevtoolsHint } from "@/components/sms/DevtoolsHint";

const LOGIN_URL = "https://accounts.google.com/AccountChooser?continue=https://messages.google.com/web/config";

const Step = ({ n, children }) => (
  <li className="flex gap-2.5">
    <span className="w-5 h-5 shrink-0 rounded-full bg-[#385C4B] text-white text-[11px] grid place-items-center font-semibold mt-0.5">{n}</span>
    <div className="flex-1 min-w-0 space-y-2">{children}</div>
  </li>
);

export const GmailDialog = ({ open, onClose, onSent }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/sms/connect-gmail", { text });
      setText("");
      onSent();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="sms-gmail-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Mail className="w-5 h-5 text-[#385C4B]" /> Connect with Gmail</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#6B6B61]">Sirf ek baar, laptop ke Chrome se. Uske baad laptop band kar sakte ho.</p>
        <ol className="space-y-4 text-sm text-[#3A3A35]">
          <Step n={1}>
            Laptop Chrome me <b>Incognito window</b> kholo (<b>Ctrl+Shift+N</b>). Ye link waha paste karke <b>wahi Gmail</b> se login karo jo phone ke Google Messages me hai.
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate text-[11px] bg-[#FAFAF6] border border-[#E0DFD6] rounded px-2 py-1.5" data-testid="sms-gmail-login-url">{LOGIN_URL}</code>
              <button onClick={() => copyText(LOGIN_URL, "link")} className="h-8 px-3 rounded-full bg-[#385C4B] text-white text-xs inline-flex items-center gap-1" data-testid="sms-gmail-copy-url">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <p className="text-[11px] text-[#6B6B61]">Login ke baad page pe thoda sa text dikhega, bas wahi page chahiye.</p>
          </Step>
          <Step n={2}>
            Usi page pe <b>F12</b> dabao → upar <b>Network</b> → <b>F5</b> dabao → list me <b>config</b> pe right-click → <b>Copy</b> → <b>Copy as cURL (bash)</b>.
            <DevtoolsHint />
          </Step>
          <Step n={3}>
            Yahan paste karke <b>Connect</b> dabao.
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="sms-gmail-input"
              placeholder="curl 'https://messages.google.com/web/config' -H ... -b 'SID=...'"
              className="w-full rounded-lg border border-[#E0DFD6] bg-[#FAFAF6] p-2.5 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" />
          </Step>
          <Step n={4}>
            Phir yahan ek <b>emoji</b> aayega. Phone pe Google Messages me popup aayega, <b>wahi emoji tap</b> karo. Uske baad Incognito window band kar do, <b>Sign out mat dabana</b>.
          </Step>
        </ol>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="h-9 px-4 rounded-full border border-[#E0DFD6] text-sm" data-testid="sms-gmail-cancel">Cancel</button>
          <button onClick={send} disabled={busy || !text.trim()} data-testid="sms-gmail-connect"
            className="h-9 px-5 rounded-full bg-[#385C4B] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform active:scale-95">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Connect
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
